import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, SchoolLevel } from '@jeraisonne/db';
import type { AppStudentObjective, ObjectiveProgress } from './objective-types';
import { getEpreuveTargetCount } from './objective-types';
import { getExerciseIdsForObjective } from './objective-filters';
import { getEpreuvesForObjective } from './epreuve-lib';
import {
  fetchMyObjectivesProgress,
  fetchObjectiveScope,
  progressFromCards,
} from './objective-scope';

/**
 * Objectif relié aux SKILLCARDS (migration 0514) : tous les objectifs
 * « exercices » sauf l'ancien diagnostic (exercices `is_diagnostic`).
 */
export function isCardsObjective(objective: AppStudentObjective): boolean {
  return objective.template.contentType !== 'epreuves' && objective.template.type !== 'diagnostic';
}

/**
 * Progression de plusieurs objectifs : UNE requête pour tous les objectifs
 * SKILLCARDS (`get_my_objectives_progress`), les autres un par un.
 */
export async function getObjectivesProgress(
  supabase: SupabaseClient<Database>,
  studentId: string,
  objectives: AppStudentObjective[],
  studentLevel?: SchoolLevel | null,
): Promise<ObjectiveProgress[]> {
  const cardObjectives = objectives.filter(isCardsObjective);
  const rows = cardObjectives.length > 0 ? await fetchMyObjectivesProgress() : [];
  const byId = new Map(rows.map((r) => [r.objective_id, r]));
  return Promise.all(
    objectives.map(async (o) => {
      if (!isCardsObjective(o)) return getObjectiveProgress(supabase, studentId, o, studentLevel);
      const row = byId.get(o.id);
      return progressFromCards(
        o.id,
        o.template.type,
        row?.progress ?? { card_count: 0, mastered_count: 0, avg_mastery: 0, hora: 0 },
        row?.pack_titles ?? [],
      );
    }),
  );
}

export async function getObjectiveProgress(
  supabase: SupabaseClient<Database>,
  studentId: string,
  objective: AppStudentObjective,
  studentLevel?: SchoolLevel | null,
): Promise<ObjectiveProgress> {
  if (objective.template.contentType === 'epreuves') {
    return getEpreuveObjectiveProgress(supabase, studentId, objective);
  }
  if (isCardsObjective(objective)) {
    const scope = await fetchObjectiveScope(objective.id);
    return progressFromCards(
      objective.id,
      objective.template.type,
      scope.progress,
      scope.packs.map((p) => p.title),
    );
  }
  return getExerciseObjectiveProgress(supabase, studentId, objective, studentLevel);
}

async function getExerciseObjectiveProgress(
  supabase: SupabaseClient<Database>,
  studentId: string,
  objective: AppStudentObjective,
  studentLevel?: SchoolLevel | null,
): Promise<ObjectiveProgress> {
  const exerciseIds = await getExerciseIdsForObjective(supabase, objective, studentLevel);
  const total = exerciseIds.length;

  if (total === 0) {
    return {
      objectiveId: objective.id,
      templateType: objective.template.type,
      totalExercises: 0,
      completedExercises: 0,
      completionPercent: 0,
      masteryPercent: 0,
    };
  }

  const { data: attempts } = await supabase
    .from('attempts')
    .select('exercise_id, score')
    .eq('student_id', studentId)
    .eq('status', 'completed')
    .in('exercise_id', exerciseIds);

  const bestByExercise = new Map<string, number>();
  for (const a of attempts ?? []) {
    if (a.exercise_id === null) continue;
    const current = bestByExercise.get(a.exercise_id) ?? 0;
    if ((a.score ?? 0) > current) {
      bestByExercise.set(a.exercise_id, a.score ?? 0);
    }
  }

  const completedExercises = bestByExercise.size;
  const completionPercent = Math.round((completedExercises / total) * 100);
  const masteryPercent =
    completedExercises === 0
      ? 0
      : Math.round(
          Array.from(bestByExercise.values()).reduce((sum, s) => sum + s, 0) / completedExercises,
        );

  return {
    objectiveId: objective.id,
    templateType: objective.template.type,
    totalExercises: total,
    completedExercises,
    completionPercent,
    masteryPercent,
  };
}

async function getEpreuveObjectiveProgress(
  supabase: SupabaseClient<Database>,
  studentId: string,
  objective: AppStudentObjective,
): Promise<ObjectiveProgress> {
  const targetCount = getEpreuveTargetCount(objective.customFilters);
  const epreuves = await getEpreuvesForObjective(supabase, objective.template);

  if (epreuves.length === 0) {
    return {
      objectiveId: objective.id,
      templateType: objective.template.type,
      totalExercises: 0,
      completedExercises: 0,
      completionPercent: 0,
      masteryPercent: 0,
      targetEpreuveCount: targetCount,
      engagedEpreuveCount: 0,
    };
  }

  const { data: links } = await supabase
    .from('epreuve_exercises')
    .select('epreuve_id, exercise_id')
    .in(
      'epreuve_id',
      epreuves.map((e) => e.id),
    );

  const exerciseToEpreuve = new Map<string, string>();
  const exercisesByEpreuve = new Map<string, Set<string>>();
  for (const link of links ?? []) {
    exerciseToEpreuve.set(link.exercise_id, link.epreuve_id);
    const set = exercisesByEpreuve.get(link.epreuve_id) ?? new Set<string>();
    set.add(link.exercise_id);
    exercisesByEpreuve.set(link.epreuve_id, set);
  }

  const allExerciseIds = Array.from(exerciseToEpreuve.keys());
  if (allExerciseIds.length === 0) {
    return {
      objectiveId: objective.id,
      templateType: objective.template.type,
      totalExercises: 0,
      completedExercises: 0,
      completionPercent: 0,
      masteryPercent: 0,
      targetEpreuveCount: targetCount,
      engagedEpreuveCount: 0,
    };
  }

  const { data: attempts } = await supabase
    .from('attempts')
    .select('exercise_id, status, score')
    .eq('student_id', studentId)
    .in('exercise_id', allExerciseIds);

  const engagedEpreuveIds = new Set<string>();
  const bestByExercise = new Map<string, number>();
  for (const a of attempts ?? []) {
    if (a.exercise_id === null) continue;
    const epreuveId = exerciseToEpreuve.get(a.exercise_id);
    if (epreuveId) engagedEpreuveIds.add(epreuveId);
    if (a.status === 'completed') {
      const current = bestByExercise.get(a.exercise_id) ?? 0;
      if ((a.score ?? 0) > current) {
        bestByExercise.set(a.exercise_id, a.score ?? 0);
      }
    }
  }

  let totalExercises = 0;
  for (const id of engagedEpreuveIds) {
    totalExercises += exercisesByEpreuve.get(id)?.size ?? 0;
  }

  const completedExercises = bestByExercise.size;
  const completionPercent =
    totalExercises === 0 ? 0 : Math.round((completedExercises / totalExercises) * 100);
  const masteryPercent =
    completedExercises === 0
      ? 0
      : Math.round(
          Array.from(bestByExercise.values()).reduce((sum, s) => sum + s, 0) / completedExercises,
        );

  return {
    objectiveId: objective.id,
    templateType: objective.template.type,
    totalExercises,
    completedExercises,
    completionPercent,
    masteryPercent,
    targetEpreuveCount: targetCount,
    engagedEpreuveCount: engagedEpreuveIds.size,
  };
}
